import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { pluginRegistry, Task } from 'make-traffic-integration-core';
import {
  FlexColumn,
  FlexRow,
  RedirectTaskControls,
  useTextFormatter,
  FilledButton,
  StyledSvg,
  Text
} from '@make-software/tg-mini-app-ui';

import CloseIcon from '@/shared/assets/icons/ic-close.svg';
import {
  ButtonWrapper,
  CloseIconButton,
  EarnArea,
  IconWrapper,
  ModalContentWrapper,
  ModalDescription,
  StyledContent,
  StyledHeader,
  getTaskIconUrl,
  StyledBodyText,
  StyledBodyTextMono
} from '@/shared';
import GoldIcon from '@/shared/assets/icons/gold.svg';
import { TaskCustomMetadata } from '@/entities';

interface TaskContentProps {
  drawerTask: Task;
  onClearDrawer: () => void;
  onStartTask: () => void;
  onClaimTask: () => void;
}

const getGoButtonTitle = (task: Task): string =>
  (task.customMetadata as TaskCustomMetadata | null)?.goButton ?? 'Start';

export const TaskContent = ({
  drawerTask,
  onClearDrawer,
  onStartTask,
  onClaimTask
}: TaskContentProps) => {
  const formatter = useTextFormatter();
  const iconUrl = useMemo(
    () => getTaskIconUrl(drawerTask, (window as any)?.config?.assets_path),
    [drawerTask]
  );
  const isRedirect = drawerTask.plugin.id === pluginRegistry.redirect.pluginID;

  const isRecruit = drawerTask.category.includes('recruit');
  const isNoStartBtn = drawerTask.category.includes('no_start_btn');

  const rewardValue = formatter.formatPoints(drawerTask.rewards[0].value);
  const theme = useTheme();


  return (
    <>
      <ModalContentWrapper direction="column">
        <StyledHeader align="center" justify="space-between">
          <FlexRow gap={8} align="center">
            <IconWrapper>
              <StyledSvg src={iconUrl} size={16} fillColor="#fff" />
            </IconWrapper>
            <StyledBodyText weight={1} size="md">
              {drawerTask.name}
            </StyledBodyText>
          </FlexRow>
          <CloseIconButton
            iconSrc={CloseIcon}
            onClick={onClearDrawer}
            size={35}
            backgroundColor={'#30313B'}
            borderColor={'#30313B'}
            borderRadius="50%"
            iconColor="white"
          />
        </StyledHeader>
        <StyledContent>
          <EarnArea>
            Earn:
            <FlexRow align={'center'} gap={'5px'}>
              <StyledSvg src={GoldIcon} fillColor="#F1BF0B" size={16} />{' '}
              <span>
                <Text weight={3} monotype>
                  {rewardValue}
                </Text>
              </span>
              {/*{drawerTask.onchain && <OnchainBadge>ON-CHAIN</OnchainBadge>}*/}
            </FlexRow>
          </EarnArea>
          <FlexColumn gap={'12px'}>
            <ModalDescription>{drawerTask.description}</ModalDescription>
          </FlexColumn>
        </StyledContent>
      </ModalContentWrapper>
      <ButtonWrapper direction="column">
        {isRedirect ? (
          <RedirectTaskControls
            task={drawerTask}
            onStartTask={onStartTask}
            onClaimTask={onClaimTask}
            rewardValue={rewardValue}
            earnIconSrc={GoldIcon}
            earnIconProps={{ fillColor: theme.colors.goldIconColor }}
            startButtonProps={{
              fontFamily: 'Inter',
              hover: {backgroundColor: theme.colors.filledBtnHover, border: '0px'}
          }}
            claimButtonProps={{
              fontFamily: 'Inter',
              base: {backgroundColor: '#30313B', color: 'white', border: '0px'},
              hover: {backgroundColor: theme.colors.filledBtnHover, border: '0px'},
              disabledStyles: {backgroundColor: theme.colors.filledBtnDisabled, color: theme.colors.filledBtnDisabledText, border: '0px'}
          }}
            rewardTextProps={{ fontFamily: 'JetBrains Mono, monospace' }}
          />
        ) : (
          <>
            {!(isRecruit || isNoStartBtn) &&<FilledButton
              fontFamily={'Inter'}
              title={
                <FlexRow align={'center'} gap={'5px'}>
                  {getGoButtonTitle(drawerTask)}
                  {Boolean(rewardValue) && (
                    <>
                      <StyledSvg src={GoldIcon} fillColor={theme.colors.goldIconColor} size={16} />
                      <StyledBodyTextMono
                        weight={3}
                        fontSize="14px"
                        color="white"
                        style={{ marginTop: '2px' }}
                      >
                        {rewardValue}
                      </StyledBodyTextMono>
                    </>
                  )}
                </FlexRow>
              }
              onClick={onStartTask}
              borderRadius="4px"
            />}
            <FilledButton
              fontFamily={'Inter'}
              title="Check"
              onClick={onClaimTask}
              base={{ backgroundColor: theme.colors.filledBtn, color: '#FFF', border: '0px' }}
              borderRadius="4px"
              hover={{backgroundColor: theme.colors.filledBtnHover, border: '0px'}}
            />
          </>
        )}
      </ButtonWrapper>
    </>
  );
};
